<template>
  <div class="detail-container">
    <div class="header">
      <!-- 返回按钮 -->
      <span class="back-btn" @click="goBack">&lt;</span>
      <h1>{{ detailData.cpmc }}</h1>
    </div>

    <div class="detail-wrapper">
      <!-- 左列 -->
      <div class="detail-column">
        <DetailItem label="批准文号" :value="detailData.pzwh" />
        <DetailItem label="有效期至" :value="detailData.yxqz" />
        <DetailItem label="申请人">
          <template #value>
            <a
              v-if="detailData.sqrzwmc"
              :href="'https://www.qcc.com/web/search?key=' + encodeURIComponent(detailData.sqrzwmc)"
              target="_blank"
              class="value qcc-link"
            >{{ detailData.sqrzwmc }}</a>
            <span v-else class="value">—</span>
          </template>
        </DetailItem>
        <DetailItem label="申请人地址" :value="detailData.sqrdz" />
        <DetailItem label="保健功能" :value="detailData.bjgn" />
        <DetailItem label="功效成分" :value="detailData.gxcf" />
        <DetailItem label="食用方法" :value="detailData.spffjsyl" />
        <DetailItem label="主要原料" :value="detailData.zyyl" />
        <DetailItem label="适宜人群" :value="detailData.syrq" />
        <DetailItem label="不适宜人群" :value="detailData.bsyrq" />
        <DetailItem label="产品规格" :value="detailData.cpgg" />
      </div>

      <!-- 右列 -->
      <div class="detail-column">
        <DetailItem label="保质期" :value="detailData.bzq" />
        <DetailItem label="贮藏方法" :value="detailData.czff" />
        <DetailItem label="注意事项" :value="detailData.zysx" />
        <DetailItem label="技术要求" :value="detailData.cpjsyq" />
        <DetailItem label="批准日期" :value="detailData.pzrq" />
        <DetailItem label="变更内容" :value="detailData.bgnr" />
        <DetailItem label="备案内容" :value="detailData.banr" />
        <DetailItem label="备案日期" :value="detailData.barq" />
        <DetailItem label="转让方" :value="detailData.zrfzwmc" />
        <DetailItem label="受让方" :value="detailData.srf" />
        <a v-if="detailData.pdfUrl" :href="localPdfUrl" target="_blank" class="pdf-link" @click.prevent="handlePdfClick">查看附件</a>
      </div>
    </div>

    <!-- 个人备注区：同一用户对同一批文的备注，可修改 -->
    <div class="note-section">
      <div class="note-header">
        <span class="note-title">我的备注</span>
        <button v-if="loggedIn" class="note-edit-btn" @click="openNoteEdit">编辑</button>
      </div>
      <div v-if="!loggedIn" class="note-login-tip">
        <span>登录后可查看和编辑备注</span>
        <a href="javascript:;" class="note-login-link" @click="handleLogin">登录</a>
      </div>
      <div v-else-if="noteContent" class="note-content">{{ noteContent }}</div>
      <div v-else class="note-empty">暂无备注，点击右上角"编辑"添加</div>
    </div>

    <!-- 备注编辑对话框 -->
    <el-dialog v-model="noteDialog.visible" title="编辑备注" width="480px" append-to-body>
      <el-input
        v-model="noteDialog.content"
        type="textarea"
        :rows="5"
        placeholder="请输入备注内容"
      />
      <template #footer>
        <el-button @click="noteDialog.visible = false">取消</el-button>
        <el-button type="primary" @click="saveNote">保存</el-button>
      </template>
    </el-dialog>

    <div v-if="loading" class="loading">加载完成</div>
    <div v-if="error" class="error">加载失败: {{ error }}</div>
  </div>
</template>

<script setup>
import config from '../config.js'

import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import DetailItem from '../components/DetailItem.vue'
import axios from 'axios'
import {
  isLoggedIn,
  requireLogin,
  isUnauthorized,
  getPendingAction,
  clearPendingAction
} from '../utils/loginGuard.js'

const route = useRoute()
const router = useRouter()
const detailData = ref({})
const loading = ref(true)
const error = ref(null)
const id = route.params.id
const isWxMini = /miniProgram/i.test(navigator.userAgent)
const loggedIn = ref(isLoggedIn())

// 个人备注状态
const noteContent = ref('')
const noteDialog = ref({ visible: false, content: '' })

// 已登录请求头
const authHeaders = () => {
  const token = localStorage.getItem('token')
  return token ? { Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}` } : {}
}

// 登录后是否自动打开备注编辑框（由 onMounted 根据待办任务设置）
let pendingNoteOpen = false

// 加载当前用户的备注
const fetchNote = () => {
  if (!loggedIn.value || !detailData.value.infosharId) {
    noteContent.value = ''
    return
  }
  axios.get(`${config.API_BASE_URL}/api/note/get`, {
    params: { infosharId: detailData.value.infosharId },
    headers: authHeaders()
  }).then(resp => {
    noteContent.value = (resp.data.data || {}).content || ''
    if (pendingNoteOpen) {
      pendingNoteOpen = false
      noteDialog.value = { visible: true, content: noteContent.value }
    }
  }).catch(() => {
    noteContent.value = ''
  })
}

// 未登录跳转登录页，登录后自动回到详情页并打开备注编辑
const handleLogin = () => {
  const current = router.currentRoute.value.fullPath
  requireLogin(router, current, {
    type: 'note-open',
    infosharId: detailData.value.infosharId || '',
    bjspwzId: id,
    cpmc: detailData.value.cpmc || ''
  })
}

// 打开备注编辑对话框
const openNoteEdit = () => {
  if (!requireLogin(router, router.currentRoute.value.fullPath)) {
    return
  }
  noteDialog.value = { visible: true, content: noteContent.value }
}

// 保存备注（未登录先登录，登录后自动保存）
const saveNote = () => {
  const infosharId = detailData.value.infosharId
  const content = noteDialog.value.content || ''
  if (!infosharId) {
    ElMessage.warning('该批文缺少唯一标识，无法保存备注')
    return
  }
  if (!requireLogin(router, router.currentRoute.value.fullPath, {
    type: 'note-save',
    infosharId,
    content
  })) {
    return
  }
  axios.post(`${config.API_BASE_URL}/api/note/save`, { infosharId, content }, {
    headers: authHeaders()
  }).then(() => {
    ElMessage.success('备注已保存')
    noteContent.value = content
    noteDialog.value.visible = false
  }).catch(error => {
    if (isUnauthorized(error)) {
      ElMessage.warning('登录已过期，请重新登录')
      requireLogin(router, router.currentRoute.value.fullPath, {
        type: 'note-save',
        infosharId,
        content
      })
      return
    }
    ElMessage.error('保存失败，请重试')
  })
}

// 本地PDF访问URL
const localPdfUrl = computed(() => {
  if (detailData.value.infosharId) {
    return `${config.API_BASE_URL}/api/pdf/${detailData.value.infosharId}`
  }
  return ''
})

// 返回上一页
const goBack = () => {
  router.go(-1)
}

// 处理PDF链接点击
const handlePdfClick = () => {
  const infosharId = detailData.value.infosharId
  if (!infosharId) return

  if (isWxMini) {
    // 小程序环境：跳转到原生PDF预览页
    if (window.wx && window.wx.miniProgram) {
      window.wx.miniProgram.navigateTo({
        url: `/pages/pdf-viewer/pdf-viewer?infosharId=${infosharId}`
      })
    } else {
      // 降级：复制链接
      const url = localPdfUrl.value
      fallbackCopy(url)
    }
  } else {
    // 非小程序环境：正常打开本地PDF
    window.open(localPdfUrl.value, '_blank')
  }
}

const fallbackCopy = (text) => {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  try {
    document.execCommand('copy')
    alert('附件链接已复制，请在浏览器中打开：\n' + text)
  } catch (e) {
    alert('请长按复制链接：' + text)
  }
  document.body.removeChild(textarea)
}

const fetchDetail = async () => {
  try {
    loading.value = true
    const response = await axios.get(`${config.API_BASE_URL}/api/getBjspById?id=${id}`)
    detailData.value = response.data.data
    // 数据加载完成后，确保滚动到页面顶部
    window.scrollTo(0, 0)
    fetchNote()
  } catch (err) {
    console.error('获取批文详情失败:', err)
    error.value = err.message
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  // 页面加载时滚动到顶部
  window.scrollTo(0, 0)
  // 登录后自动续做：打开备注编辑对话框
  const pending = getPendingAction()
  if (pending && pending.type === 'note-open') {
    clearPendingAction()
    pendingNoteOpen = true
  }
  fetchDetail()
})
</script>

<style scoped>
.detail-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 30px;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 10;
  padding: 10px 0;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #f0f0f0;
  color: #333;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s;
  flex-shrink: 0;
}

.back-btn:hover {
  background: #e0e0e0;
}

.back-btn:active {
  background: #d0d0d0;
}

h1 {
  margin: 0;
  font-size: 24px;
  flex: 1;
}

.detail-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 20px;
}

.detail-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.loading,
.error {
  text-align: center;
  padding: 20px;
  margin-top: 30px;
}

.error {
  color: #f56c6c;
}

.pdf-link {
  margin-top: 20px;
  padding: 12px;
  background: #eaeaf0;
  border-radius: 4px;
  text-align: center;
  color: #02060e;
  text-decoration: none;
  display: block;
}

/* 个人备注区 */
.note-section {
  margin-top: 24px;
  padding: 16px 20px;
  background: #fafbfc;
  border: 1px solid #ebeef5;
  border-radius: 8px;
}

.note-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.note-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
}

.note-edit-btn {
  background: #409eff;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 5px 16px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.note-edit-btn:hover {
  background: #66b1ff;
}

.note-login-tip {
  font-size: 14px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 10px;
}

.note-login-link {
  color: #409eff;
  font-weight: 600;
  text-decoration: none;
  padding: 4px 18px;
  border: 1px solid #409eff;
  border-radius: 16px;
  transition: all 0.3s ease;
}

.note-login-link:hover {
  background: #409eff;
  color: #fff;
}

.note-content {
  font-size: 14px;
  color: #303133;
  line-height: 1.6;
  background: #fff;
  border-radius: 6px;
  padding: 10px 14px;
  white-space: pre-wrap;
  word-break: break-word;
}

.note-empty {
  font-size: 13px;
  color: #b0b3b8;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .detail-container {
    padding: 10px;
  }

  .header {
    flex-wrap: nowrap;
    gap: 10px;
  }

  .back-btn {
    width: 36px;
    height: 36px;
    font-size: 20px;
  }

  h1 {
    font-size: 20px;
    width: 100%;
  }

  .detail-wrapper {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .detail-column {
    gap: 12px;
  }

  .pdf-link {
    min-height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.qcc-link {
  color: #1976D2;
  cursor: pointer;
  text-decoration: none;
  word-break: break-word;
}
.qcc-link:hover {
  text-decoration: underline;
}
</style>
